import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Mail } from 'lucide-react';

const PricingTier = ({ name, price, period, description, features, highlighted = false, badge = null, cta }) => (
  <div className={`apple-panel p-5 sm:p-6 rounded-apple relative flex flex-col ${highlighted ? 'ring-2 ring-sortio-primary' : ''}`}>
    {badge && (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full bg-sortio-primary/80 backdrop-blur-md border border-white/20 shadow-lg">
        <span className="text-xs font-medium text-white">{badge}</span>
      </div>
    )}

    <div className={`flex flex-col flex-grow ${badge ? 'pt-4' : ''}`}>
      <h3 className="text-lg font-semibold text-apple-50 mb-1">{name}</h3>
      <div className="flex items-baseline mb-1">
        <span className="text-apple-300 text-base mr-1">$</span>
        <span className="text-3xl font-bold text-apple-50">{price}</span>
        {period && <span className="text-apple-400 ml-1 text-xs">{period}</span>}
      </div>
      <p className="text-apple-400 text-xs mb-4">{description}</p>

      <ul className="list-none space-y-2 mb-4 flex-grow">
        {features.map((item, index) => (
          <li key={index} className="flex items-start">
            <div className="flex-shrink-0 w-4 h-4 rounded-full bg-sortio-primary/20 flex items-center justify-center mr-2 mt-0.5">
              <Check className="w-2.5 h-2.5 text-sortio-primary" />
            </div>
            <span className="text-apple-200 text-xs leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>

      {cta && <div className="mt-auto">{cta}</div>}
    </div>
  </div>
);

const SmallBusinessPricing = () => {
  return (
    <section id="pricing" className="my-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Simple pricing
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Most bookkeepers start with Pro. When the whole back office is ready to standardize
          vendor filing, Team adds shared rules and admin controls across seats.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
        <PricingTier
          name="Free"
          price="0"
          period=""
          description="Try it on one month of invoices"
          features={[
            'One-time AI trial allowance, enough to try Sortio on a real folder',
            'Rule-based sorting: free and unlimited',
            'Preview before applying',
            'Sort history & undo',
            'Local LLM / BYOK',
          ]}
          cta={
            <Link to="/download" className="block">
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-apple-200 bg-apple-600/30 border border-apple-500/30 hover:bg-apple-600/50 transition-all">
                Download Free
              </button>
            </Link>
          }
        />

        <PricingTier
          name="Pro"
          price="14.99"
          period="/mo or $99/yr"
          description="For bookkeepers and admins"
          badge="Most Popular"
          highlighted={true}
          features={[
            'Monthly AI allowance sized for heavy use',
            'Vendor & project routing',
            'AI sort: up to 5,000 files / run',
            'Auto-sort on file change',
            'Bring your own AI key: unmetered, no allowance used',
            'Usage shown as a simple percentage in Settings',
            'Unlimited file renaming',
            'Email support (48h)',
          ]}
          cta={
            <Link to="/download" className="block">
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-sortio-primary hover:bg-sortio-primary/90 transition-all shadow-lg shadow-sortio-primary/25">
                Get Pro
              </button>
            </Link>
          }
        />

        <PricingTier
          name="Team"
          price="29"
          period="/seat/mo"
          description="Shared workflows for the back office"
          features={[
            'Everything in Pro',
            'Double the Pro AI allowance per seat, pooled across your team',
            'Add seats to grow your shared allowance',
            'Shared automations & rules',
            'Admin console & seats',
            'Centralized LLM policy',
            'Priority support (24h)',
          ]}
          cta={
            <Link to="/checkout/team" className="block">
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-sortio-primary/90 hover:bg-sortio-primary transition-all shadow-lg shadow-sortio-primary/20">
                Get Team
              </button>
            </Link>
          }
        />

        <PricingTier
          name="Enterprise"
          price="50+"
          period="/seat/mo"
          description="For larger rollouts"
          features={[
            'Everything in Team',
            'Volume licensing (25+ seats)',
            'Audit logs',
            'Centralized billing and custom invoicing',
            'Priority support',
            'SSO or dedicated deployment: ask us',
          ]}
          cta={
            <a
              href="mailto:marcus@getsortio.com?subject=Sortio for Back Office - Enterprise Inquiry"
              className="block"
            >
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-apple-200 bg-apple-600/30 border border-apple-500/30 hover:bg-apple-600/50 transition-all flex items-center justify-center gap-1.5">
                Contact Sales <Mail className="w-3.5 h-3.5" />
              </button>
            </a>
          }
        />
      </div>
    </section>
  );
};

export default SmallBusinessPricing;
